import VueJwtDecode from 'vue-jwt-decode'

// Self-healing localStorage helpers.
//
// The checkout boots by reading persisted values (an auth token, a cached
// merchant-info JSON blob, the selected store slug) inside the Vuex store and
// the router's navigation guard. A single corrupt value there - a truncated
// JSON blob from an interrupted write, or a malformed token - used to throw
// during boot and leave the customer on a permanent blank screen, because the
// guard had no try/catch and there is no router error boundary. Clearing site
// data was the only escape, which is why a refresh or switching networks never
// helped.
//
// These helpers never throw: localStorage access itself can fail (storage
// disabled / private mode / quota), so every call is guarded. When a stored
// value is unusable, the offending key is removed so the app recovers on the
// next load instead of staying broken.

export function getStorageItem(key) {
  try {
    return localStorage.getItem(key)
  } catch (e) {
    return null
  }
}

export function setStorageItem(key, value) {
  try {
    localStorage.setItem(key, value)
  } catch (e) {
    // Storage disabled or over quota - nothing we can do, and it must not crash.
  }
}

export function removeStorageItem(key) {
  try {
    localStorage.removeItem(key)
  } catch (e) {
    // ignore
  }
}

// Reads a JSON blob from localStorage. A corrupt / truncated value is deleted
// and treated as absent, so it self-heals on the next write instead of
// permanently breaking the page.
export function parseStoredJSON(key) {
  const raw = getStorageItem(key)
  if (typeof raw !== 'string') return null
  try {
    return JSON.parse(raw)
  } catch (e) {
    removeStorageItem(key)
    return null
  }
}

// Decodes a JWT, returning null instead of throwing on a malformed token.
// Pass `clearKey` to drop the bad token from storage so the app recovers as
// logged-out and fetches a fresh one.
export function safeJwtDecode(token, { clearKey } = {}) {
  if (!token) return null
  try {
    // vue-jwt-decode throws on an unparseable token but returns null for a
    // structurally invalid one (fewer than 2 segments); treat both as bad.
    const decoded = VueJwtDecode.decode(token)
    if (!decoded && clearKey) removeStorageItem(clearKey)
    return decoded ?? null
  } catch (e) {
    if (clearKey) removeStorageItem(clearKey)
    return null
  }
}
